import Cookie from 'js-cookie'
import React, { PropsWithChildren, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Caption1, Heading2 } from '@ui/Typography'

import { ApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { getTickmillPublicDomain } from '@utils/companyName.utils'
import { loginToAuth } from '@utils/loginToAuth'
import { CommonPaths } from '@utils/path-common'
import { TokenStorage } from '@utils/tokenStorage'

import { clearLocalStorage, safeLocalStorage } from '@hooks/useStorage'

import { Box } from '@shared/components/Box'
import { Loading } from '@shared/components/Loading/Loading'

const BE_ACCESS_COOKIE = '__Host-tm-access-token'
const BE_REFRESH_COOKIE = '__Host-tm-refresh-token'

const isImpersonationEntry = (): boolean => {
  if (!window.location.pathname.includes('/auth')) {
    return false
  }
  const params = new URLSearchParams(window.location.search)
  return !!params.get('token') && !!params.get('clientId')
}

const clearAllSessionState = () => {
  clearLocalStorage()
  TokenStorage.clear()
  Cookie.remove('auth')
  Cookie.remove(BE_ACCESS_COOKIE, { path: '/', secure: true })
  Cookie.remove(BE_REFRESH_COOKIE, { path: '/', secure: true })
}

const impersonate = async (): Promise<string> => {
  const params = new URLSearchParams(window.location.search)
  const token = params.get('token')!
  const clientId = params.get('clientId')!

  clearAllSessionState()

  const apiClient = new ClientApiClient(new ApiClient())
  const data = await apiClient.impersonatorLogin(clientId, getTickmillPublicDomain(), token)

  const auth = loginToAuth(data)
  if (!auth) {
    throw new Error('Impersonation response is missing session data.')
  }

  const cookieOpts = { secure: true, path: '/', sameSite: 'Lax' as const }
  if (auth.accessToken) {
    Cookie.set(BE_ACCESS_COOKIE, auth.accessToken, cookieOpts)
  }
  if (auth.refreshToken) {
    Cookie.set(BE_REFRESH_COOKIE, auth.refreshToken, cookieOpts)
  }
  TokenStorage.storeSession(auth.sessionId)
  safeLocalStorage.setJSON('auth', auth)

  return CommonPaths.Dashboard
}

export const ImpersonationGate: React.FC<PropsWithChildren> = ({ children }) => {
  const [gateActive] = useState(isImpersonationEntry)
  const [error, setError] = useState<string | null>(null)
  const { t } = useTranslation()

  useEffect(() => {
    if (!gateActive) return

    impersonate()
      .then((target) => {
        window.location.replace(target)
      })
      .catch((e: unknown) => {
        clearAllSessionState()
        const message =
          (e as any)?.response?.data?.message || 'Impersonation login failed. Please try again.'
        setError(message)
      })
  }, [gateActive])

  if (!gateActive) {
    return <>{children}</>
  }

  if (error) {
    return (
      <Box p='x40' textAlign='center'>
        <Heading2>{t('errors.Something went wrong')}</Heading2>
        <Caption1 color='text-danger-high-em'>{error}</Caption1>
      </Box>
    )
  }

  return <Loading isLoading showLoadingIcon />
}
